import { Grid } from '@mui/material';
import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { dateFormatShortDate, toDayEnd } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalPalette,
  RoutalSearchInput,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  VSpacer,
} from '@nx-smartmonkey/ui';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Range } from 'react-date-range';
import { useNavigate } from 'react-router-dom';

import { PlanActionsMenu } from '../../../components/menus/PlanActionsMenu';
import * as routing from '../../../constants/Routing';
import { ProjectPlansTableColumnKeys } from '../../../context/plan/tableColumns';
import { sortDataByTableColumns, TableColumnsSortConfig } from '../../../context/shared/tableColumns';
import { PlanFlat } from '../../../domain/plan/PlanFlat';
import { useRetrieveFlatPlans } from '../../../hooks/plan/useRetrieveFlatPlans';
import { useSearchPlans } from '../../../hooks/plan/useSearchPlans';
import { useSearchPlansPredicates } from '../../../hooks/plan/useSearchPlansPredicates';
import { useSetProjectPlansColumns } from '../../../hooks/plan/useSetProjectPlansColumns';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import Button from '../../../react-components/Button';
import { DataTable } from '../../../react-components/data-table/DataTable';
import { DateRangePicker } from '../../../react-components/date-range-picker/DateRangePicker';
import Text from '../../../react-components/Text';
import { CreatePlan } from './CreatePlan';
import { getPlannerPlansColumns } from './PlannerPlansListColumns';
import { PlannerPlansListColumnsButton } from './PlannerPlansListColumnsButton';

const LIMIT = 20;

export const PlannerPlansList = () => {
  const navigate = useNavigate();
  const { data: activeProject } = useRetrieveActiveProject();
  const { predicates, setPredicates } = useSearchPlansPredicates();

  const [selectedFlatPlans, setSelectedFlatPlans] = useState<Array<PlanFlat>>([]);
  const [searchParameters, setSearchParameters] = useState<{ limit: number; offset: number }>({
    offset: 0,
    limit: LIMIT,
  });
  const [tableContextualAnchor, setTableContextualAnchor] = useState<
    { screenPosition: { top: number; left: number }; plan: PlanFlat } | undefined
  >(undefined);
  const [datePickerPosition, setDatePickerPosition] = useState<{ top: number; left: number } | undefined>(undefined);

  const filterStatus = useMemo(() => predicates.find((p) => p.field === `status`), [predicates]);
  const startDatePredicate = predicates.find((p) => p.field === `execution_date` && p.operator === `gte`);
  const startDate = useMemo(
    () =>
      startDatePredicate?.value
        ? new Date(startDatePredicate.value as string)
        : moment(new Date()).subtract(`1`, `month`).toDate(),
    [startDatePredicate]
  );
  const endDatePredicate = predicates.find((p) => p.field === `execution_date` && p.operator === `lte`);
  const endDate = useMemo(
    () => (endDatePredicate?.value ? new Date(endDatePredicate.value as string) : new Date()),
    [endDatePredicate]
  );
  const searchTextPredicate = predicates.find((p) => p.field === `label`);
  const searchText = (searchTextPredicate?.value ?? ``) as string;

  const [sortPlansInfo, setSortPlansInfo] = useState<TableColumnsSortConfig<ProjectPlansTableColumnKeys>>({
    orderBy: `execution_date`,
    order: `desc`,
  });

  const { data: flatPlans, run: retrieveFlatPlans } = useRetrieveFlatPlans({ project: activeProject });

  const {
    data: plansPagination,
    run: getPlans,
    isLoading,
  } = useSearchPlans({
    project: activeProject,
    offset: searchParameters.offset,
    limit: searchParameters.limit,
  });

  const { tableColumns } = useSetProjectPlansColumns();

  useEffect(() => {
    if (searchParameters.offset !== 0) {
      // Reset pagination because dates range has changed.
      setSearchParameters({ offset: 0, limit: LIMIT });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    if (predicates.length > 0) {
      getPlans();
      retrieveFlatPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParameters]);

  if (!plansPagination || !activeProject) {
    return null;
  }

  return (
    <>
      <Grid
        container
        style={{
          width: `100%`,
          zIndex: 2,
          backgroundColor: `white`,
          minWidth: `1080px`,
          borderRadius: `10px`,
          border: `1px solid ${RoutalPalette.neutral10}`,
        }}
        wrap="nowrap"
      >
        <div
          style={{
            height: `100%`,
            width: `100%`,
          }}
        >
          <Grid container direction="column" wrap="nowrap" style={{ padding: `0 20px` }}>
            <Grid container direction="column" style={{ padding: `12px 0` }}>
              <Grid container item justifyContent="space-between" direction="row" wrap="nowrap">
                <Grid container item direction="row" wrap="nowrap" alignItems="center">
                  <Grid item>
                    <Button
                      id="date-picker-button"
                      styleType="secondary"
                      variant="flat"
                      size="xs"
                      style={{
                        backgroundColor: `transparent`,
                        height: `26px`,
                        borderColor: RoutalPalette.neutral20,
                        borderRadius: `5px`,
                      }}
                      onClick={(e: any) => {
                        const datePickerButton = document.getElementById(`date-picker-button`);
                        if (!datePickerButton) return;

                        const { x, bottom } = datePickerButton.getBoundingClientRect();
                        setDatePickerPosition({ top: bottom, left: x });
                      }}
                    >
                      <Grid
                        container
                        item
                        justifyContent="space-between"
                        direction="row"
                        wrap="nowrap"
                        alignItems="center"
                      >
                        <Grid item>
                          <Text style={{ cursor: `pointer` }}>{`${dateFormatShortDate(
                            startDate
                          )} - ${dateFormatShortDate(endDate)}`}</Text>
                        </Grid>
                      </Grid>
                    </Button>
                  </Grid>

                  <HSpacer medium />

                  <RoutalSelect
                    id="status-filter"
                    containerStyle={{
                      maxWidth: `200px`,
                    }}
                    popoverProps={{
                      options: [`all_the_plans`, `in_progress`, `planning`, `finished`, `draft`].map(
                        (status, index): RoutalSelectMultiSelectOption => ({
                          key: status,
                          onClick: () => {
                            setPredicates([
                              ...predicates.filter((p) => p.field !== `status`),
                              ...(index !== 0
                                ? [
                                    SearchPredicate.create({
                                      field: `status`,
                                      operator: `eq`,
                                      type: `string`,
                                      value: status,
                                    }),
                                  ]
                                : []),
                            ]);
                          },
                          content: (
                            <Grid
                              container
                              direction="row"
                              wrap="nowrap"
                              alignItems="center"
                              style={{ overflow: `hidden` }}
                            >
                              <Text
                                tagName="span"
                                style={{
                                  textAlign: `justify`,
                                  whiteSpace: `nowrap`,
                                  overflow: `hidden`,
                                  width: `100%`,
                                  textOverflow: `ellipsis`,
                                  cursor: `pointer`,
                                }}
                                intlId={`planner.plans.${status}`}
                              />
                            </Grid>
                          ),
                        })
                      ),
                    }}
                  >
                    <Text
                      style={{
                        cursor: `pointer`,
                        width: `100%`,
                        textAlign: `justify`,
                      }}
                      intlId={`planner.plans.${filterStatus?.value ?? `all_the_plans`}`}
                    />
                  </RoutalSelect>
                </Grid>

                <Grid item container direction="row" justifyContent="flex-end" alignItems="center" wrap="nowrap">
                  <RoutalSearchInput
                    inputContainerStyles={{
                      width: `260px`,
                    }}
                    fieldValue={searchText}
                    onChange={_.debounce((text: any) => {
                      setPredicates([
                        ...predicates.filter((p) => p.field !== `label`),
                        ...(text
                          ? [
                              SearchPredicate.create({
                                field: `label`,
                                operator: `contains`,
                                type: `string`,
                                value: text,
                              }),
                            ]
                          : []),
                      ]);
                    }, 300)}
                    searchLive
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                justifyContent="space-between"
                direction="row"
                wrap="nowrap"
                style={{ height: `50px` }}
              >
                <Grid container alignItems="center">
                  {selectedFlatPlans.length === 0 ? (
                    <Text intlId="planner.plans.num_plans" intlValues={{ total: (flatPlans ?? []).length }} />
                  ) : (
                    <Text
                      intlId="planner.plans.num_selected_stops"
                      intlValues={{ num: selectedFlatPlans.length, total: (flatPlans ?? []).length }}
                    />
                  )}

                  <HSpacer medium />

                  <Grid item>
                    {selectedFlatPlans.length > 0 ? (
                      <PlanActionsMenu
                        type="popover"
                        showSettings={false}
                        plans={selectedFlatPlans}
                        popoverProps={{
                          hideArrow: false,
                          style: {
                            justifyContent: `space-between`,
                          },
                          containerStyle: {
                            width: `200px`,
                          },
                          content: <Text variant="body" style={{ cursor: `pointer` }} intlId="planner.plan_actions" />,
                        }}
                        onDeletePlan={(deletedPlans: Array<PlanFlat>) => {
                          const deletedPlansIds = deletedPlans.map((deletedPlan) => deletedPlan.id);
                          setSelectedFlatPlans(
                            selectedFlatPlans.filter((selectedPlan) => !deletedPlansIds.includes(selectedPlan.id))
                          );
                        }}
                      />
                    ) : null}
                  </Grid>
                </Grid>
                <Grid item container alignItems="center" justifyContent="flex-end">
                  <PlannerPlansListColumnsButton id="plans-configurable-table-columns" />
                </Grid>
              </Grid>
            </Grid>

            {isLoading || plansPagination.total > 0 ? (
              <>
                <DataTable
                  loading={isLoading}
                  docs={sortDataByTableColumns({
                    data: plansPagination.docs ?? [],
                    tableColumns,
                    sortInfo: sortPlansInfo,
                  }).map((doc) => ({
                    doc,
                    checkboxIcon: { element: <div style={{ width: `24px` }} /> },
                  }))}
                  maxHeight="100vh"
                  total={plansPagination.total}
                  limit={plansPagination.limit}
                  page={plansPagination.page}
                  pages={plansPagination.pages}
                  emptyMessage={<Text intlId="no_results" />}
                  handleOnContextMenu={(ev: any, plan: PlanFlat) => {
                    ev.preventDefault();
                    setTableContextualAnchor({ screenPosition: { top: ev.clientY, left: ev.clientX }, plan });
                  }}
                  selectedIds={selectedFlatPlans.map((flatPlan) => flatPlan.id)}
                  onMultipleSelection={({ selectedIds, fromGlobalCheckbox }) => {
                    if (fromGlobalCheckbox) {
                      if (selectedFlatPlans.length > 0) {
                        setSelectedFlatPlans([]);
                      } else {
                        setSelectedFlatPlans(flatPlans ?? []);
                      }
                    } else {
                      const flatPlansSelection = flatPlans?.filter((flatPlan) =>
                        (selectedIds ?? []).includes(flatPlan.id)
                      );
                      setSelectedFlatPlans(flatPlansSelection ?? []);
                    }
                  }}
                  // onRowDoubleClick={(doc) =>
                  //   activeProject &&
                  //   navigate(
                  //     routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, activeProject.id).replace(
                  //       `:planId`,
                  //       doc.id
                  //     )
                  //   )
                  // }
                  onChangePage={({ offset, limit }) =>
                    setSearchParameters({
                      ...searchParameters,
                      offset,
                      limit,
                    })
                  }
                  initialSortBy={sortPlansInfo.orderBy}
                  initialSortOrder={sortPlansInfo.order}
                  onColumnSort={(column: any, order: `asc` | `desc`) => {
                    setSortPlansInfo({ order, orderBy: column });
                  }}
                  columns={getPlannerPlansColumns({
                    activeProject,
                    tableColumns,
                    onDeletePlan: (deletedPlans: Array<PlanFlat>) => {
                      const deletedPlansIds = deletedPlans.map((deletedPlan) => deletedPlan.id);
                      setSelectedFlatPlans(
                        selectedFlatPlans.filter((selectedPlan) => !deletedPlansIds.includes(selectedPlan.id))
                      );
                    },
                  })}
                />

                {tableContextualAnchor ? (
                  <PlanActionsMenu
                    type="contextual"
                    showSettings={false}
                    plans={[tableContextualAnchor.plan]}
                    contextAnchor={tableContextualAnchor}
                    setContextAnchor={() => {
                      setTableContextualAnchor(undefined);
                    }}
                    onDeletePlan={(deletedPlans: Array<PlanFlat>) => {
                      const deletedPlansIds = deletedPlans.map((deletedPlan) => deletedPlan.id);
                      setSelectedFlatPlans(
                        selectedFlatPlans.filter((selectedPlan) => !deletedPlansIds.includes(selectedPlan.id))
                      );
                    }}
                  />
                ) : null}
              </>
            ) : (
              <Grid
                container
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{
                  height: `100%`,
                  padding: `32px`,
                  paddingTop: `0`,
                }}
              >
                <Grid container direction="column" justifyContent="center" style={{ textAlign: `center` }}>
                  <Text variant="h6" isBold intlId="planner.plans.no_plans_in_filter.title" />

                  <VSpacer medium />

                  <Text variant="body-medium" intlId="planner.plans.no_plans_in_filter.info" />
                </Grid>

                <VSpacer medium />
                <Grid container justifyContent="center">
                  <CreatePlan
                    buttonProps={{
                      id: `create-plan-button`,
                      styleType: `secondary`,
                    }}
                    handleCreatePlan={(newPlanId: string) => {
                      navigate(
                        routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, activeProject!.id).replace(
                          `:planId`,
                          newPlanId
                        )
                      );
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </div>
      </Grid>

      <DateRangePicker
        id="select-dates"
        popoverPosition={datePickerPosition}
        startDate={startDate ? new Date(startDate) : undefined}
        endDate={endDate ? new Date(endDate) : undefined}
        onClose={() => {
          setDatePickerPosition(undefined);
        }}
        onUpdate={(range: Range) => {
          setPredicates([
            ...predicates.filter((p) => p.field !== `execution_date`),
            ...(range.startDate
              ? [
                  SearchPredicate.create({
                    field: `execution_date`,
                    operator: `gte`,
                    type: `date`,
                    value: range.startDate.toUTCString(),
                  }),
                ]
              : []),
            ...(range.endDate
              ? [
                  SearchPredicate.create({
                    field: `execution_date`,
                    operator: `lte`,
                    type: `date`,
                    value: toDayEnd(range.endDate).toUTCString(),
                  }),
                ]
              : []),
          ]);
        }}
      />
    </>
  );
};
